/**
 * useMonthlyCutsLoader Hook
 * 
 * Loads and manages monthly cuts data from the attendance service
 * Provides filtered options for year and month dropdowns
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { attendanceService } from '@/api/domains/attendance';
import type { MonthlyCutsHoldingScope } from '@/api/domains/attendance/services/monthlyCuts.service';
import {
  filterMonthlyCutsByRange,
  generateYearOptions,
  generateMonthOptions,
} from '@/api/domains/attendance/mappers/monthlyCuts.mapper';
import type { MonthlyCut } from '@/api/domains/attendance';


export interface UseMonthlyCutsLoaderOptions {
  /**
   * Whether to show an error notification when the backend returns
   * NOT_FOUND_MONTHLY_CUTS (no cuts available for the given scope).
   * Default true. Screens where "no cuts for this scope" is an expected,
   * non-error state (e.g. switching companies in a holding filter) should
   * pass false so the dropdowns simply render empty without a toast.
   */
  notifyOnEmpty?: boolean;
}

export const useMonthlyCutsLoader = (
  scope?: MonthlyCutsHoldingScope,
  options?: UseMonthlyCutsLoaderOptions,
) => {
  const notifyOnEmpty = options?.notifyOnEmpty ?? true;
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const { createSignal } = useAbortController();

  // Monthly cuts state
  const [allMonthlyCuts, setAllMonthlyCuts] = useState<MonthlyCut[]>([]);
  const [loadingMonthlyCuts, setLoadingMonthlyCuts] = useState(false);

  const scopeKey = `${scope?.filterCompanyId ?? ''}|${scope?.allCompanies ?? ''}`;

  const loadMonthlyCuts = useCallback(async () => {
    setLoadingMonthlyCuts(true);
    const signal = createSignal();

    try {
      const response = await attendanceService.getMonthlyCutsNew(scope, signal);
      if (response.error?.code === 'ABORTED') return;

      if (response.success && response.data) {
        setAllMonthlyCuts(response.data.cuts);
      } else if (response.error?.code === 'NOT_FOUND_MONTHLY_CUTS') {
        setAllMonthlyCuts([]);
        if (notifyOnEmpty) {
          showApiError(response, translate('attendance:overtimeReport.errors.noMonthlyCuts'));
        }
      } else {
        showApiError(response, translate('attendance:overtimeReport.errors.loadMonthlyCuts'));
      }
    } catch {
      if (!signal.aborted) {
        showNotification('error', translate('attendance:overtimeReport.errors.loadMonthlyCuts'), 5000);
      }
    } finally {
      if (!signal.aborted) {
        setLoadingMonthlyCuts(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotification, showApiError, translate, createSignal, scopeKey, notifyOnEmpty]);

  // Load monthly cuts on mount and whenever the holding scope changes
  useEffect(() => {
    void loadMonthlyCuts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Filter cuts to last 60 months using pure function from mapper
  const monthlyCutsWithinRange = useMemo(
    () => filterMonthlyCutsByRange(allMonthlyCuts),
    [allMonthlyCuts],
  );

  // Generate year options using pure function from mapper
  const yearOptions = useMemo(
    () => generateYearOptions(monthlyCutsWithinRange),
    [monthlyCutsWithinRange],
  );

  // Generate month options function using pure function from mapper
  const monthOptions = useMemo(
    () => (selectedYear: number) => generateMonthOptions(monthlyCutsWithinRange, selectedYear),
    [monthlyCutsWithinRange],
  );

  return {
    loadingMonthlyCuts,
    monthlyCutsWithinRange,
    yearOptions,
    monthOptions,
  };
};
